import { ReactElement, useState } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { Button, TextField, Typography } from '@mui/material'
import type { Auth0UserDTO } from '@api/fastAPI.schemas'
import {
  getGetUnassignedAuth0UsersQueryKey,
  useCreateAuth0User,
} from '@api/user-management/user-management'
import { useQueryClient } from '@tanstack/react-query'
import { SaveButton } from '@ui/actions'
import { DialogBase } from '@ui/feedback'
import { useSnackbar } from '@ui/snackbar'
import { Box } from '@ui/structure'

interface Props {
  open: boolean
  onClose: () => void
  onCreated: (user: Auth0UserDTO) => void
}

const CreateAuth0UserDialog = ({ open, onClose, onCreated }: Props): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const [email, setEmail] = useState('')
  const [name, setName] = useState('')

  const { mutate: createAuth0User, isLoading } = useCreateAuth0User<AxiosError, unknown>({
    mutation: {
      meta: {
        title: 'Auth0 Nutzer erstellen',
        description: 'Erstellt einen neuen Benutzer in Auth0',
      },
      onSuccess: async createdUser => {
        await queryClient.invalidateQueries(getGetUnassignedAuth0UsersQueryKey())
        enqueueSnackbar('Auth0 Nutzer erfolgreich erstellt', { variant: 'success' })
        onCreated(createdUser)
      },
      onError: error => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Erstellen des Auth0 Nutzers'), {
          variant: 'error',
        })
      },
    },
  })

  const canSubmit = email.trim().length > 0 && name.trim().length > 0

  return (
    <DialogBase
      open={open}
      onClose={onClose}
      title="Auth0 Nutzer erstellen"
      dialogProps={{ maxWidth: 'sm', fullWidth: true }}
    >
      <Box display="flex" flexDirection="column" gap={2} pt={1}>
        <TextField
          label="E-Mail"
          size="small"
          value={email}
          onChange={event => setEmail(event.target.value)}
          data-cy="input-auth0-email"
        />
        <TextField
          label="Name"
          size="small"
          value={name}
          onChange={event => setName(event.target.value)}
          data-cy="input-auth0-name"
        />
        <Typography variant="caption" color="text.secondary">
          Der Nutzer setzt sein Passwort über &quot;Passwort vergessen&quot; auf der Login-Seite.
        </Typography>
      </Box>
      <>
        <Button size="small" onClick={onClose}>
          Abbrechen
        </Button>
        <SaveButton
          size="small"
          loading={isLoading}
          disabled={!canSubmit}
          onClick={() => createAuth0User({ data: { email: email.trim(), name: name.trim() } })}
          data-cy="btn-save-auth0-user"
        >
          Erstellen
        </SaveButton>
      </>
    </DialogBase>
  )
}

export default CreateAuth0UserDialog
